import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, List, ListOrdered, Pilcrow, Undo2, Redo2 } from 'lucide-react';
import { useEffect } from 'react';

export function RichTextEditor({ value, onChange, placeholder = '', testId = 'rich-text-editor' }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class:
          'rich-text-content min-h-[140px] px-3 py-2 focus:outline-none',
        'data-placeholder': placeholder,
        'data-testid': `${testId}-area`,
      },
    },
  });

  // Sync external value changes (e.g. when editing a different item)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (current !== incoming && current !== '<p></p>') {
      editor.commands.setContent(incoming, false);
    } else if (!incoming && current !== '<p></p>') {
      editor.commands.setContent('', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const ToolbarButton = ({ active, onClick, label, icon: Icon, testId }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={label}
      className={`w-8 h-8 flex items-center justify-center rounded-sm border text-xs transition-colors ${
        active
          ? 'bg-[#ED2227] border-[#ED2227] text-white'
          : 'bg-transparent border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500'
      }`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
    </button>
  );

  return (
    <div
      className="border border-zinc-800 rounded-md bg-zinc-950 overflow-hidden"
      data-testid={testId}
    >
      <div className="flex flex-wrap gap-1 px-2 py-2 border-b border-zinc-800 bg-zinc-900/40">
        <ToolbarButton
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="Bold"
          icon={Bold}
          testId={`${testId}-bold`}
        />
        <ToolbarButton
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="Bullet list"
          icon={List}
          testId={`${testId}-bullet`}
        />
        <ToolbarButton
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="Numbered list"
          icon={ListOrdered}
          testId={`${testId}-ordered`}
        />
        <ToolbarButton
          active={editor.isActive('paragraph')}
          onClick={() => editor.chain().focus().setParagraph().run()}
          label="Paragraph"
          icon={Pilcrow}
          testId={`${testId}-paragraph`}
        />
        <div className="w-px bg-zinc-800 mx-1" />
        <ToolbarButton
          active={false}
          onClick={() => editor.chain().focus().undo().run()}
          label="Undo"
          icon={Undo2}
          testId={`${testId}-undo`}
        />
        <ToolbarButton
          active={false}
          onClick={() => editor.chain().focus().redo().run()}
          label="Redo"
          icon={Redo2}
          testId={`${testId}-redo`}
        />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
